import React, { useState, useEffect } from "react";
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import { useContext } from "react";
import { LanguageContext } from "../Context/LanguageContext";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo_image from "../assets/Logo.png";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const [nav, setNav] = useState(false);
  const [scrollMove, setScrollMove] = useState(false);
  const [color, setColor] = useState('transparent');
  const [textColor, setTextColor] = useState('white');

  const handleNav = () => {
    setNav(!nav);
  };

  const scrollToSection = (sectionId) => {
    // Close mobile menu first
    if (nav) {
      setNav(false);
    }
    
    if (sectionId == "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const section = document.getElementById(sectionId);
    if (section) {
      const yOffset = -100;
      const y =
        section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const changeColor = () => {
      if (window.scrollY >= 90) {
        setColor("rgba(0, 0, 0, 0.7)");
        setTextColor("white");
        setScrollMove(true);
      } else {
        setColor("transparent");
        setTextColor("white");
        setScrollMove(false);
      }
    };

    // Initial call
    changeColor();

    window.addEventListener("scroll", changeColor);

    return () => {
      window.removeEventListener("scroll", changeColor);
    };
  }, [path]);
  
  const { texts, language, changeLanguage } = useContext(LanguageContext);

  const toggleLanguage = (lang) => {
    changeLanguage(lang);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (nav) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [nav]);

  return (
    <>
      <nav
        style={{ backgroundColor: `${color}` }}
        className={`fixed left-0 top-0 w-full z-40 transition-all duration-500 ${scrollMove ? 'backdrop-blur-xl shadow-lg border-b border-white/10' : 'backdrop-blur-sm'}`}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 lg:px-8 py-2 sm:py-2 lg:py-2.5 text-white">
          <a href="/" className="group flex-shrink-0">
            <img
              src={logo_image}
              className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 transition-transform duration-300 group-hover:scale-110 object-contain"
              alt="Company Logo"
            />
          </a>

          <ul className="hidden lg:flex items-center gap-1">
            <li>
              <div
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 cursor-pointer ${
                  path === "/" ? "bg-primary-400 text-white" : "text-white hover:bg-primary-400 hover:text-white"
                }`}
              >
                {path != "/" ? (
                  <a href="/">{texts.home}</a>
                ) : (
                  <Link onClick={() => scrollToSection("/")}>{texts.home}</Link>
                )}
              </div>
            </li>
            <li>
              <div
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 cursor-pointer ${
                  path === "/about" ? "bg-primary-400 text-white" : "text-white hover:bg-primary-400 hover:text-white"
                }`}
              >
                {path != "/" ? (
                  <a href="/about">{texts.about}</a>
                ) : (
                  <Link onClick={() => scrollToSection("about")}>
                    {texts.about}
                  </Link>
                )}
              </div>
            </li>
            <li>
              <div className="px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 text-white hover:bg-primary-400 hover:text-white cursor-pointer">
                {path != "/" ? (
                  <a href="/">{texts.brands}</a>
                ) : (
                  <Link onClick={() => scrollToSection("brands")}>
                    {texts.brands}
                  </Link>
                )}
              </div>
            </li>
            <li>
              <div className="px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 text-white hover:bg-primary-400 hover:text-white cursor-pointer">
                <Link onClick={() => scrollToSection("contact")}>
                  {texts.contact}
                </Link>
              </div>
            </li>

            {/* Language Switcher */}
            <li className={`${language === "en" ? "ml-4" : "mr-4"}`}>
              <div className="flex rounded-full overflow-hidden border-2 border-primary-400 bg-black/20 backdrop-blur-sm">
                <button
                  className={`font-bold px-4 py-2 text-xs transition-all duration-300 ${
                    language === "en"
                      ? "bg-primary-400 text-white"
                      : `text-white hover:bg-white/10`
                  }`}
                  onClick={() => toggleLanguage("en")}
                >
                  EN
                </button>
                <button
                  className={`font-bold px-4 py-2 text-xs transition-all duration-300 ${
                    language === "ar"
                      ? "bg-primary-400 text-white"
                      : `text-white hover:bg-white/10`
                  }`}
                  onClick={() => toggleLanguage("ar")}
                >
                  KU
                </button>
              </div>
            </li>
          </ul>

          {/* Mobile Navigation Controls */}
          <div className="flex lg:hidden items-center gap-3">
            {/* Mobile Language Switcher */}
            <div className="flex rounded-full overflow-hidden border-2 border-primary-400 bg-black/20 backdrop-blur-sm">
              <button
                className={`font-bold px-3 py-1.5 text-xs transition-all duration-300 ${
                  language === "en"
                    ? "bg-primary-400 text-white"
                    : `text-white hover:bg-white/10`
                }`}
                onClick={() => toggleLanguage("en")}
              >
                EN
              </button>
              <button
                className={`font-bold px-3 py-1.5 text-xs transition-all duration-300 ${
                  language === "ar"
                    ? "bg-primary-400 text-white"
                    : `text-white hover:bg-white/10`
                }`}
                onClick={() => toggleLanguage("ar")}
              >
                KU
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={handleNav}
              className="relative p-2 rounded-full transition-all duration-300 hover:bg-white/10 z-50"
              aria-label="Toggle menu"
            >
              {nav ? (
                <AiOutlineClose
                  className="w-6 h-6"
                  style={{ color: "white" }}
                />
              ) : (
                <AiOutlineMenu
                  className="w-6 h-6"
                  style={{ color: `${textColor}` }}
                />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Full Screen */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-all duration-500 ease-in-out ${
          nav ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        {/* Backdrop with Blur */}
        <div
          className={`absolute inset-0 backdrop-blur-lg bg-black/60 transition-all duration-500 ${
            nav ? "opacity-100" : "opacity-0"
          }`}
          onClick={handleNav}
        />

        {/* Menu Panel - Full Screen */}
        <div
          className={`relative w-full h-full bg-gradient-to-br from-neutral-900/95 via-black/95 to-neutral-800/95 backdrop-blur-xl transform transition-all duration-500 ease-in-out ${
            nav ? "translate-y-0 scale-100" : "-translate-y-full scale-95"
          }`}
        >
          {/* Header with Logo and Close Button */}
          <div className="flex items-center justify-between p-3 2xsm:p-4 xsm:p-5 sm:p-6 md:p-8 border-b border-neutral-700/50">
            <img
              src={logo_image}
              className="w-12 h-12 2xsm:w-14 2xsm:h-14 xsm:w-16 xsm:h-16 sm:w-20 sm:h-20 md:w-24 md:h-24"
              alt="Logo"
            />
            <button
              onClick={handleNav}
              className="p-2 2xsm:p-2.5 xsm:p-3 sm:p-3.5 md:p-4 rounded-full hover:bg-white/10 transition-colors duration-200"
              aria-label="Close menu"
            >
              <AiOutlineClose
                className="w-6 h-6 2xsm:w-7 2xsm:h-7 xsm:w-8 xsm:h-8 sm:w-9 sm:h-9 md:w-10 md:h-10"
                color="white"
              />
            </button>
          </div>
          
          {/* Navigation Links - Perfectly Centered */}
          <div className="flex items-center justify-center h-[calc(100%-60px)] 2xsm:h-[calc(100%-70px)] xsm:h-[calc(100%-80px)] sm:h-[calc(100%-96px)] md:h-[calc(100%-120px)] p-3 2xsm:p-4 xsm:p-5 sm:p-6 md:p-8">
            <nav className="w-full max-w-xs 2xsm:max-w-sm xsm:max-w-md sm:max-w-lg">
              <ul className="space-y-3 2xsm:space-y-4 xsm:space-y-5 sm:space-y-6 md:space-y-8">
                <li>
                  <div className={`block w-full px-3 2xsm:px-4 xsm:px-5 sm:px-6 md:px-8 py-2.5 2xsm:py-3 xsm:py-4 sm:py-5 md:py-6 text-lg 2xsm:text-xl xsm:text-2xl sm:text-3xl md:text-4xl font-bold rounded-xl sm:rounded-2xl hover:scale-105 transition-all duration-300 text-center cursor-pointer ${
                    path === "/" ? "bg-primary-400 text-white border-2 border-primary-400" : "text-white hover:bg-primary-400/20 hover:text-primary-400 border border-transparent hover:border-primary-400/30"
                  }`}>
                    {path != "/" ? (
                      <a href="/">{texts.home}</a>
                    ) : (
                      <Link onClick={() => scrollToSection("/")}>{texts.home}</Link>
                    )}
                  </div>
                </li>
                <li>
                  <div className={`block w-full px-3 2xsm:px-4 xsm:px-5 sm:px-6 md:px-8 py-2.5 2xsm:py-3 xsm:py-4 sm:py-5 md:py-6 text-lg 2xsm:text-xl xsm:text-2xl sm:text-3xl md:text-4xl font-bold rounded-xl sm:rounded-2xl hover:scale-105 transition-all duration-300 text-center cursor-pointer ${
                    path === "/about" ? "bg-primary-400 text-white border-2 border-primary-400" : "text-white hover:bg-primary-400/20 hover:text-primary-400 border border-transparent hover:border-primary-400/30"
                  }`}>
                    {path != "/" ? (
                      <a href="/about">{texts.about}</a>
                    ) : (
                      <Link onClick={() => scrollToSection("about")}>
                        {texts.about}
                      </Link>
                    )}
                  </div>
                </li>
                <li>
                  <div className="block w-full px-3 2xsm:px-4 xsm:px-5 sm:px-6 md:px-8 py-2.5 2xsm:py-3 xsm:py-4 sm:py-5 md:py-6 text-lg 2xsm:text-xl xsm:text-2xl sm:text-3xl md:text-4xl font-bold text-white rounded-xl sm:rounded-2xl hover:bg-primary-400/20 hover:text-primary-400 hover:scale-105 transition-all duration-300 text-center border border-transparent hover:border-primary-400/30 cursor-pointer">
                    {path != "/" ? (
                      <a href="/">{texts.brands}</a>
                    ) : (
                      <Link onClick={() => scrollToSection("brands")}>
                        {texts.brands}
                      </Link>
                    )}
                  </div>
                </li>
                <li>
                  <div className="block w-full px-3 2xsm:px-4 xsm:px-5 sm:px-6 md:px-8 py-2.5 2xsm:py-3 xsm:py-4 sm:py-5 md:py-6 text-lg 2xsm:text-xl xsm:text-2xl sm:text-3xl md:text-4xl font-bold text-white rounded-xl sm:rounded-2xl hover:bg-primary-400/20 hover:text-primary-400 hover:scale-105 transition-all duration-300 text-center border border-transparent hover:border-primary-400/30 cursor-pointer">
                    <Link onClick={() => scrollToSection("contact")}>
                      {texts.contact}
                    </Link>
                  </div>
                </li>
              </ul>

              {/* Mobile Menu Language Switcher - Centered at Bottom */}
              <div className="flex justify-center mt-6 2xsm:mt-8 xsm:mt-10 sm:mt-12 md:mt-16">
                <div className="flex rounded-xl sm:rounded-2xl overflow-hidden border-2 border-primary-400 bg-black/30 backdrop-blur-sm shadow-lg">
                  <button
                    className={`font-bold px-4 2xsm:px-5 xsm:px-6 sm:px-8 md:px-10 py-2 2xsm:py-2.5 xsm:py-3 sm:py-4 md:py-5 text-sm 2xsm:text-base xsm:text-lg sm:text-xl md:text-2xl transition-all duration-300 ${
                      language === "en"
                        ? "bg-primary-400 text-white scale-105"
                        : `text-white hover:bg-white/10 hover:scale-105`
                    }`}
                    onClick={() => toggleLanguage("en")}
                  >
                    EN
                  </button>
                  <button
                    className={`font-bold px-4 2xsm:px-5 xsm:px-6 sm:px-8 md:px-10 py-2 2xsm:py-2.5 xsm:py-3 sm:py-4 md:py-5 text-sm 2xsm:text-base xsm:text-lg sm:text-xl md:text-2xl transition-all duration-300 ${
                      language === "ar"
                        ? "bg-primary-400 text-white scale-105"
                        : `text-white hover:bg-white/10 hover:scale-105`
                    }`}
                    onClick={() => toggleLanguage("ar")}
                  >
                    KU
                  </button>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;